<script>
import {
  VApp, VAppBar, VAppBarTitle, VAppBarNavIcon,
  VBadge, VBtn, VCol, VFadeTransition, VFooter, VMain, VRow, VSpacer,
  VNavigationDrawer, VList, VListItem
} from "vuetify/components";
import largeLucentaryLogoUrl from "@/assets/img/lucentary-banner-blue.svg";
import smallLucentaryLogoUrl from "@/assets/img/lucentary-banner-blue.svg";
import PrivacyPolicyDialog from "@/components/PrivacyPolicyDialog.vue";
import TermsOfServiceDialog from "@/components/TermsOfServiceDialog.vue";
import { publicRoutes, selfServiceTemplateRoute, academicTemplateRoute, accountingTemplateRoute, developmentTemplateRoute, getRouter } from "@/router";
import { authenticationService, executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "./services";
import { useUserStore } from "./stores";
import { useSeoMeta, useHead } from "@unhead/vue";
import { useRoute } from "vue-router";
import { computed } from "vue";
import indexableRoutes from "@/seo/indexableRoutes.json";

const getAllowedRoutes = function(routes, account) {
	if (!routes || routes.length === 0 || !account)
		return 0;

	const allowedRoutes = routes.filter(route => {
		if (!route.meta || !route.meta.authorize?.length)
			return true;
		if (!account?.roles)
			return false;
		const allowedRoles = account.roles.filter(role => route.meta.authorize.includes(role));
		return allowedRoles.length > 0;
	});
	return allowedRoutes;
};
const getAllowedLinkCount = function (routes, account) {
	const allowedRoutes = getAllowedRoutes(routes, account);
	return allowedRoutes.length;
};

const seoLookup = Object.fromEntries(indexableRoutes.map(r => [r.name, r]));
const siteOrigin = "https://lucentary.com";

export default {
	name: "App",

	components: {
		VApp, VAppBar, VAppBarTitle, VAppBarNavIcon, VBadge, VBtn, VCol, VFadeTransition, VFooter, VMain, VRow, VSpacer,
		VNavigationDrawer, VList, VListItem,
		PrivacyPolicyDialog, TermsOfServiceDialog,
	},

	setup() {
		const route = useRoute();
		const seo = computed(() => seoLookup[route.name] || {});
		const pageTitle = computed(() => seo.value.title || "Lucentary | Senior-Led Software Consulting & Rescue");
		const pageDescription = computed(() => seo.value.description || "Senior-led software consulting that fixes broken or AI-built apps, finishes stalled products, and builds new ones right the first time.");
		const pageCanonical = computed(() => {
			const path = route.path;
			return siteOrigin + (path === "/" ? "/" : path.replace(/\/+$/, ""));
		});
		const pageRobots = computed(() => seoLookup[route.name] ? "index, follow" : "noindex, follow");

		useSeoMeta({
			title: pageTitle,
			description: pageDescription,
			ogTitle: pageTitle,
			ogDescription: pageDescription,
			ogUrl: pageCanonical,
			twitterTitle: pageTitle,
			twitterDescription: pageDescription,
			robots: pageRobots,
		});

		useHead(() => ({
			link: [
				{ rel: "canonical", href: pageCanonical.value },
				{ rel: "alternate", hreflang: "en", href: pageCanonical.value },
				{ rel: "alternate", hreflang: "x-default", href: pageCanonical.value },
			],
		}));

		return {};
	},

	async created() {
		this.userStore = useUserStore();
		this.checkActiveAccount();
	},

	beforeUnmount() {
		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", this.handleModalKeydown);
	},

	data: () => ({
		largeLucentaryLogoUrl: largeLucentaryLogoUrl,
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
		showNavigationDrawer: false,
		showPrivacyDialog: false,
		showTermsDialog: false,
		smallLucentaryLogoUrl: smallLucentaryLogoUrl,
		topNavigationLinks: [],
		userStore: null
	}),

	computed: {
		isImpersonating() {
			return this.userStore?.isImpersonating(); 
		}
	},	

	watch: {
		$route(to, from) {
			this.checkActiveAccount();
		},
		isImpersonating(to, from) {
			this.checkActiveAccount();
		},
		"requestMoreInfo.isShowing"(isShowing) {
			if (isShowing) {
				const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
				document.documentElement.style.overflow = "hidden";
				document.body.style.overflow = "hidden";

				if (scrollbarWidth > 0) {
					document.body.style.paddingRight = `${scrollbarWidth}px`;
				}

				window.addEventListener("keydown", this.handleModalKeydown);
			} else {
				document.documentElement.style.overflow = "";
				document.body.style.overflow = "";
				document.body.style.paddingRight = "";
				window.removeEventListener("keydown", this.handleModalKeydown);
			}
		}
	},

	methods: {
		checkActiveAccount() {
			const account = this.userStore.getAccount();
			if (!account) {
				this.topNavigationLinks = [];
				return;
			}

			const existingTopNavigationLinks = this.topNavigationLinks;
			const topNavigationLinks = [
				{ icon: "mdi-view-dashboard", title: "My Dashboard", routeName: "my", allowedLinkCount: getAllowedLinkCount(selfServiceTemplateRoute.children, account), hasNotifications: false },
				{ icon: "mdi-school", title: "Academics", routeName: "academicHome", allowedLinkCount: getAllowedLinkCount(academicTemplateRoute.children, account), hasNotifications: false },
				{ icon: "mdi-currency-usd", title: "Accounting", routeName: "accountingHome", allowedLinkCount: getAllowedLinkCount(accountingTemplateRoute.children, account), hasNotifications: false },
				{ icon: "mdi-code-braces", title: "Development", routeName: "developmentHome", allowedLinkCount: getAllowedLinkCount(developmentTemplateRoute.children, account), hasNotifications: false },
			];
			topNavigationLinks.forEach(topNavigationLink => {
				const existingLink = existingTopNavigationLinks.find(link => link.title === topNavigationLink.title);
				if (!!existingLink)
					topNavigationLink.hasNotifications = existingLink.hasNotifications;
			});
			this.topNavigationLinks = topNavigationLinks.filter(link => link.allowedLinkCount > 0);

			const accountingLink = this.topNavigationLinks.find(link => link.title === "Accounting");
			if (!!accountingLink) {
				executeServiceCall(selfServiceService.accounting.notifications)
					.then(({ data }) => accountingLink.hasNotifications = !!data?.length)
					.catch(error => console.error(error));
			}

		},
		async getAccountingHasNotifications() {
			return true;
		},
		async getNoNotifications() {
			return false;
		},
		signIn() {
			this.userStore.clearAccount();
			getRouter().push({ name: "initiatesignin" });
		},
		signOut() {
			authenticationService.signOut(
				 () => {
					this.userStore.clearAccount();
					this.checkActiveAccount();
				},
				error => console.error(error));
		},
		openGetStartedDialog() {
			this.showNavigationDrawer = false;
			this.requestMoreInfo.topic = "GetStarted";
			this.requestMoreInfo.isError = false;
			this.requestMoreInfo.isSuccess = false;
			this.requestMoreInfo.isProcessing = false;
			this.requestMoreInfo.validationErrors = [];
			this.requestMoreInfo.isShowing = true;
		},
		closeRequestMoreInfo() {
			this.requestMoreInfo.isShowing = false;
		},
		async submitRequestForMoreInfo() {
			this.requestMoreInfo.isProcessing = true;
			this.requestMoreInfo.isError = false;
			this.requestMoreInfo.validationErrors = [];
			this.requestMoreInfo.contactInfo.phoneNumber = "n/a";
			this.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

			await executeServiceCall(() => selfServiceService.public.submitContactRequest(this.requestMoreInfo.contactInfo))
				.then(() => {
					this.requestMoreInfo.isSuccess = true;
				})
				.catch(error => {
					if (error?.response?.status === 400) {
						this.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
							validationMessage === "Your E-mail Address is required."
								? "Your E-mail Address or Phone Number is required."
								: validationMessage
						);
					} else {
						console.error(error);
						this.requestMoreInfo.isError = true;
					}
				})
				.finally(() => {
					this.requestMoreInfo.isProcessing = false;
				});
		},
		handleModalKeydown(event) {
			if (event.key === "Escape") {
				this.closeRequestMoreInfo();
			}
		},
	}
}
</script>

<template>
	<VApp>
		<VAppBar v-if="!$route.meta.hideChrome" app flat height="76" class="lux-appbar">
			<div class="lux-bar">
				<RouterLink :to="{ name: 'home' }" class="lux-brand" aria-label="Lucentary home">
					<img :src="largeLucentaryLogoUrl" alt="Lucentary" class="lux-logo" />
				</RouterLink>

				<nav class="lux-nav hide-below-939" aria-label="Primary">
					<RouterLink :to="{ name: 'services' }" class="lux-navlink">Services</RouterLink>
					<RouterLink :to="{ name: 'about' }" class="lux-navlink">About</RouterLink>
					<RouterLink :to="{ name: 'how-we-work' }" class="lux-navlink">How We Work</RouterLink>
					<RouterLink :to="{ name: 'faq' }" class="lux-navlink">FAQ</RouterLink>
					<RouterLink :to="{ name: 'resources' }" class="lux-navlink">Resources</RouterLink>
					<RouterLink :to="{ name: 'contact' }" class="lux-navlink">Contact Us</RouterLink>
				</nav>

				<div class="lux-actions hide-below-939">
					<RouterLink v-for="link in topNavigationLinks" :key="link.title" :to="{ name: link.routeName }" :title="link.title" class="lux-icon" :class="`${link.routeName}-action`">
						<VBadge v-if="link.hasNotifications" color="error" dot>
							<i :class="`mdi ${link.icon}`"></i>
						</VBadge>
						<i v-else :class="`mdi ${link.icon}`"></i>
					</RouterLink>

					<button v-if="!userStore.isSignedIn()" type="button" class="lux-ghost signin" @click="signIn">Sign In</button>
					<button v-if="userStore.isSignedIn()" type="button" class="lux-ghost signout" @click="signOut">Sign Out</button>
					<button type="button" class="lux-cta" @click="openGetStartedDialog">Get Started</button>
				</div>

				<VAppBarNavIcon
					class="show-below-939 lux-burger"
					color="primary"
					aria-label="Open navigation"
					@click="showNavigationDrawer = !showNavigationDrawer"
				/>
			</div>
		</VAppBar>

		<VNavigationDrawer
			v-model="showNavigationDrawer"
			temporary
			location="right"
			width="300"
			class="lux-drawer"
		>
			<div class="lux-drawer-inner">
				<div class="lux-drawer-head">
					<img :src="largeLucentaryLogoUrl" alt="Lucentary" class="lux-drawer-logo" />
					<button type="button" class="lux-drawer-close" aria-label="Close navigation" @click="showNavigationDrawer = false">&times;</button>
				</div>

				<nav class="lux-drawer-nav" aria-label="Mobile">
					<RouterLink class="lux-drawer-link" :to="{ name: 'services' }" @click="showNavigationDrawer = false">Services &amp; Pricing</RouterLink>
					<RouterLink class="lux-drawer-link" :to="{ name: 'about' }" @click="showNavigationDrawer = false">About</RouterLink>
					<RouterLink class="lux-drawer-link" :to="{ name: 'how-we-work' }" @click="showNavigationDrawer = false">How We Work</RouterLink>
					<RouterLink class="lux-drawer-link" :to="{ name: 'faq' }" @click="showNavigationDrawer = false">FAQ</RouterLink>
					<RouterLink class="lux-drawer-link" :to="{ name: 'resources' }" @click="showNavigationDrawer = false">Resources</RouterLink>
					<RouterLink class="lux-drawer-link" :to="{ name: 'contact' }" @click="showNavigationDrawer = false">Contact Us</RouterLink>
					<RouterLink v-for="(link, i) in topNavigationLinks" :key="`top-${i}`" class="lux-drawer-link" :to="{ name: link.routeName }" @click="showNavigationDrawer = false">{{ link.title }}</RouterLink>
				</nav>

				<div class="lux-drawer-actions">
					<button type="button" class="lux-cta lux-cta-block" @click="openGetStartedDialog">Get Started</button>
					<button v-if="!userStore.isSignedIn()" type="button" class="lux-ghost lux-ghost-block signin" @click="signIn">Sign In</button>
					<button v-if="userStore.isSignedIn()" type="button" class="lux-ghost lux-ghost-block signout" @click="signOut">Sign Out</button>
				</div>
			</div>
		</VNavigationDrawer>

		<VMain>
			<div class="main-content">
				<main role="main" class="pb-3">
					<RouterView v-slot="{ Component, route }">
						<VFadeTransition mode="out-in" :hide-on-leave="true">
							<div :key="route.name">
								<component :is="Component"/>
							</div>
						</VFadeTransition>
					</RouterView>
				</main>
			</div>
		</VMain>

		<VFooter v-if="!$route.meta.hideChrome" class="lux-footer">
			<div class="lux-footer-inner">
				<div class="lux-footer-top">
					<div class="lux-footer-brand">
						<RouterLink :to="{ name: 'home' }" class="lux-footer-logo-link" aria-label="Lucentary home">
							<img :src="largeLucentaryLogoUrl" alt="Lucentary" class="lux-footer-logo" />
						</RouterLink>
						<p class="lux-footer-tagline">Professional software services — built, fixed, and made to last.</p>
						<div class="lux-social">
							<a class="lux-social-link" href="https://m.facebook.com/61554542814302/" target="_blank" rel="noopener" aria-label="Facebook"><i class="mdi mdi-facebook"></i></a>
							<a class="lux-social-link" href="https://www.linkedin.com/company/lucentary-academy" target="_blank" rel="noopener" aria-label="LinkedIn"><i class="mdi mdi-linkedin"></i></a>
							<a class="lux-social-link" href="https://maps.app.goo.gl/MbnqXNZ98wS4Fz1J6" target="_blank" rel="noopener" aria-label="Google"><i class="mdi mdi-google"></i></a>
							<a class="lux-social-link" href="https://www.yelp.com/biz/lucentary-academy-fort-worth-2" target="_blank" rel="noopener" aria-label="Yelp"><img class="lux-yelp-img" src="https://s3-media0.fl.yelpcdn.com/assets/srv0/yelp_design_cdn/cabcef1daea2/assets/img/brand/burst_red.svg" alt="Yelp" /></a>
						</div>
					</div>

					<div class="lux-footer-col">
						<h3 class="lux-footer-heading">Get in touch</h3>
						<button type="button" class="lux-footer-link lux-footer-link-button" @click="openGetStartedDialog">Get Started</button>
						<RouterLink :to="{ name: 'curriculum' }" class="lux-footer-link lux-footer-link-button" aria-label="Curriculum">Curriculum</RouterLink>
						<button type="button" class="lux-footer-link lux-footer-link-button" @click="showTermsDialog = true">Terms of Use</button>
						<button type="button" class="lux-footer-link lux-footer-link-button" @click="showPrivacyDialog = true">Privacy Policy</button>
					</div>

					<div class="lux-footer-col lux-footer-col-seal">
						<a href="https://www.bbb.org/us/tx/fort-worth/profile/computer-software-developers/lucentary-academy-inc-0825-1000230038/#sealclick" target="_blank" rel="nofollow" aria-label="Lucentary BBB Business Review">
							<img class="lux-bbb" src="https://seal-austin.bbb.org/seals/blue-seal-200-42-bbb-1000230038.png" alt="Lucentary BBB Business Review" />
						</a>
					</div>
				</div>

				<div class="lux-footer-bottom">
					<p class="lux-copy">&copy; {{ new Date().getFullYear() }} Lucentary Academy Inc. All rights reserved.</p>
				</div>
			</div>
		</VFooter>

		<PrivacyPolicyDialog v-model="showPrivacyDialog" />
		<TermsOfServiceDialog v-model="showTermsDialog" />

		<transition name="modal-fade">
			<div v-if="requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeRequestMoreInfo">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="getStartedModalTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeRequestMoreInfo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
					<div class="modal-scroll">
						<template v-if="!requestMoreInfo.isSuccess">
							<span class="modal-badge">Free · No commitment</span>
							<h2 id="getStartedModalTitle" class="modal-title">Let's get started</h2>
							<p class="modal-description">Tell us a bit about your project and we'll reach out to get things moving.</p>
							<div class="modal-field">
								<label for="getStartedName">Name</label>
								<input id="getStartedName" v-model="requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="getStartedEmail">Email or phone number</label>
								<input id="getStartedEmail" v-model="requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="getStartedText">What's going on with your software?</label>
								<textarea id="getStartedText" v-model="requestMoreInfo.contactInfo.text" rows="4" :disabled="requestMoreInfo.isProcessing"></textarea>
							</div>
							<div v-if="requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
								<p class="modal-feedback-title">Please fix the following:</p>
								<ul class="modal-feedback-list">
									<li v-for="(validationError, validationErrorIndex) in requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
								</ul>
							</div>
							<p v-else-if="requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your message. Please try again.</p>
							<button type="button" class="btn btn-primary btn-block" :disabled="requestMoreInfo.isProcessing" @click="submitRequestForMoreInfo"><span v-if="requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ requestMoreInfo.isProcessing ? "Sending…" : "Send message" }}</button>
						</template>
						<div v-else class="modal-success">
							<span class="success-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<h2 class="modal-title">Message received</h2>
							<p class="modal-description">Thanks for reaching out — we'll be in touch with you shortly.</p>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</VApp>
</template>

<style scoped>
	/* ===== Shared responsive visibility (around 939px) ===== */
	@media (max-width: 939.98px) {
		.hide-below-939 { display: none !important; }
		.show-below-939 { display: inline-flex !important; }
	}
	@media (min-width: 940px) {
		.hide-below-939 { display: flex !important; }
		.show-below-939 { display: none !important; }
	}

	/* ===== Navbar ===== */
	.lux-appbar.v-app-bar {
		background: rgba(255, 255, 255, 0.82) !important;
		backdrop-filter: blur(14px) saturate(160%);
		-webkit-backdrop-filter: blur(14px) saturate(160%);
		border-bottom: 1px solid #e6edf8;
		box-shadow: 0 10px 30px -24px rgba(15, 33, 66, 0.45);
	}

	.lux-appbar :deep(.v-toolbar__content) {
		padding: 0;
		height: 76px !important;
	}

	.lux-bar {
		display: flex;
		align-items: center;
		gap: 1rem;
		flex: 1 1 100%;
		max-width: 1280px;
		margin-inline: auto;
		padding: 0 clamp(1rem, 3vw, 2rem);
		height: 100%;
		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.lux-brand {
		display: inline-flex;
		align-items: center;
		flex-shrink: 0;
		text-decoration: none;
	}

	.lux-logo {
		height: clamp(44px, 5.5vw, 54px);
		width: auto;
		display: block;
	}

	.lux-nav {
		flex: 1;
		align-items: center;
		justify-content: center;
		gap: 0.25rem;
	}

	.lux-navlink {
		position: relative;
		display: inline-flex;
		align-items: center;
		padding: 0.5rem 0.95rem;
		border-radius: 10px;
		font-size: 0.96rem;
		font-weight: 600;
		letter-spacing: 0.1px;
		color: #344256;
		text-decoration: none;
		transition: color 0.18s ease, background 0.18s ease;
	}

	.lux-navlink::after {
		content: "";
		position: absolute;
		left: 50%;
		bottom: 0.3rem;
		width: 0;
		height: 2px;
		border-radius: 2px;
		background: #2563eb;
		transform: translateX(-50%);
		transition: width 0.22s ease;
	}

	.lux-navlink:hover {
		color: #1d4ed8;
		background: rgba(37, 99, 235, 0.06);
	}

	.lux-navlink.router-link-active {
		color: #1d4ed8;
	}

	.lux-navlink.router-link-active::after {
		width: 18px;
	}

	.lux-actions {
		align-items: center;
		gap: 0.4rem;
		flex-shrink: 0;
	}

	.lux-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 42px;
		height: 42px;
		border-radius: 12px;
		color: #475569;
		text-decoration: none;
		transition: background 0.18s ease, color 0.18s ease;
	}

	.lux-icon:hover {
		background: rgba(37, 99, 235, 0.08);
		color: #1d4ed8;
	}

	.lux-icon .mdi {
		font-size: 22px;
		line-height: 1;
	}

	.lux-ghost {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.5rem 0.95rem;
		border: none;
		background: transparent;
		font-family: inherit;
		font-size: 0.95rem;
		font-weight: 600;
		color: #344256;
		border-radius: 10px;
		cursor: pointer;
		transition: color 0.18s ease, background 0.18s ease;
	}

	.lux-ghost:hover {
		color: #1d4ed8;
		background: rgba(37, 99, 235, 0.06);
	}

	.lux-cta {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.62rem 1.35rem;
		margin-left: 0.3rem;
		border-radius: 999px;
		background: #2563eb;
		color: #ffffff;
		font-family: inherit;
		font-size: 0.95rem;
		font-weight: 600;
		text-decoration: none;
		white-space: nowrap;
		border: none;
		cursor: pointer;
		box-shadow: 0 12px 24px -12px rgba(37, 99, 235, 0.7);
		transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
	}

	.lux-cta:hover {
		transform: translateY(-1px);
		background: #1d4ed8;
		box-shadow: 0 16px 30px -14px rgba(37, 99, 235, 0.75);
	}

	.lux-burger {
		margin-left: auto;
	}

	/* ===== Mobile drawer ===== */
	.lux-drawer {
		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.lux-drawer-inner {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		padding: 1.1rem 1.1rem 1.5rem;
	}

	.lux-drawer-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.25rem 0.25rem 1rem;
		border-bottom: 1px solid #e6edf8;
		margin-bottom: 0.85rem;
	}

	.lux-drawer-logo {
		height: 46px;
		width: auto;
		display: block;
	}

	.lux-drawer-close {
		width: 38px;
		height: 38px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: none;
		background: #f1f5fb;
		color: #475569;
		font-size: 1.6rem;
		line-height: 1;
		border-radius: 10px;
		cursor: pointer;
		transition: background 0.18s ease, color 0.18s ease;
	}

	.lux-drawer-close:hover {
		background: #e7eefe;
		color: #1d4ed8;
	}

	.lux-drawer-nav {
		display: flex;
		flex-direction: column;
		gap: 0.2rem;
	}

	.lux-drawer-link {
		display: block;
		padding: 0.8rem 0.85rem;
		border-radius: 12px;
		font-size: 1.02rem;
		font-weight: 600;
		color: #1e293b;
		text-decoration: none;
		transition: background 0.18s ease, color 0.18s ease;
	}

	.lux-drawer-link:hover {
		background: rgba(37, 99, 235, 0.07);
		color: #1d4ed8;
	}

	.lux-drawer-link.router-link-active {
		background: rgba(37, 99, 235, 0.1);
		color: #1d4ed8;
	}

	.lux-drawer-actions {
		display: flex;
		flex-direction: column;
		gap: 0.6rem;
		margin-top: 1.1rem;
		padding-top: 1.1rem;
		border-top: 1px solid #e6edf8;
	}

	.lux-cta-block {
		width: 100%;
		margin-left: 0;
		padding: 0.85rem 1rem;
	}

	.lux-ghost-block {
		width: 100%;
		padding: 0.78rem 1rem;
		border: 1px solid #cdd9ec;
		border-radius: 999px;
	}

	/* ===== Footer ===== */
	.lux-footer.v-footer {
		display: block;
		padding: 0 !important;
		background: #ffffff !important;
		border-top: 1px solid #e6edf8;
		color: #475569;
		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.lux-footer-inner {
		width: 100%;
		max-width: 1240px;
		margin: 0 auto;
		padding: clamp(2.5rem, 5vw, 4rem) clamp(1.25rem, 4vw, 2.5rem) 1.5rem;
	}

	.lux-footer-top {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		justify-content: space-between;
		gap: clamp(2rem, 5vw, 3.5rem);
		padding-bottom: 2.25rem;
		border-bottom: 1px solid #eef2f9;
	}

	.lux-footer-brand {
		flex: 0 1 360px;
		max-width: 360px;
	}

	.lux-footer-logo-link {
		display: inline-flex;
	}

	.lux-footer-logo {
		height: clamp(50px, 5vw, 60px);
		width: auto;
		display: block;
		margin-left: -18px;
		margin-bottom: 1rem;
	}

	.lux-footer-tagline {
		font-size: 0.96rem;
		line-height: 1.6;
		color: #6f7f99;
		margin: 0 0 1.25rem;
	}

	.lux-social {
		display: flex;
		gap: 0.6rem;
	}

	.lux-social-link {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		border-radius: 12px;
		background: #f1f5fb;
		color: #475569;
		text-decoration: none;
		transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
	}

	.lux-social-link:hover {
		background: #2563eb;
		color: #ffffff;
		transform: translateY(-2px);
	}

	.lux-social-link .mdi {
		font-size: 20px;
		line-height: 1;
	}

	.lux-footer-col {
		display: flex;
		flex-direction: column;
		flex: 0 0 auto;
	}

	.lux-footer-heading {
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: #0f2142;
		margin: 0 0 1rem;
	}

	.lux-footer-link {
		font-size: 0.96rem;
		font-weight: 500;
		color: #56657d;
		text-decoration: none;
		padding: 0.32rem 0;
		transition: color 0.18s ease;
	}

	.lux-footer-link:hover {
		color: #1d4ed8;
	}

	.lux-footer-link-button {
		background: none;
		border: none;
		font-family: inherit;
		text-align: left;
		cursor: pointer;
	}

	.lux-yelp-img {
		width: 22px;
		height: 22px;
		display: block;
		filter: grayscale(1);
		transition: filter 0.18s ease;
	}

	.lux-social-link:hover .lux-yelp-img {
		filter: brightness(0) invert(1);
	}

	.lux-footer-col-seal {
		align-items: flex-start;
	}

	.lux-bbb {
		height: 42px;
		width: auto;
		display: block;
		border: 0;
	}

	.lux-footer-bottom {
		display: flex;
		align-items: center;
		justify-content: center;
		padding-top: 1.5rem;
	}

	.lux-copy {
		font-size: 0.88rem;
		color: #8190a5;
		margin: 0;
		text-align: center;
	}

	@media (max-width: 960px) {
		.lux-footer-brand {
			flex: 1 1 100%;
			max-width: none;
		}
	}

	@media (max-width: 560px) {
		.lux-footer-top {
			flex-direction: column;
			align-items: center;
			justify-content: flex-start;
			text-align: center;
		}

		.lux-footer-brand {
			display: flex;
			flex-direction: column;
			align-items: center;
		}

		.lux-footer-logo {
			margin-left: 0;
		}

		.lux-social {
			justify-content: center;
		}

		.lux-footer-col {
			align-items: center;
		}

		.lux-footer-col-seal {
			align-items: center;
		}
	}

	/* ===== Get Started dialog ===== */
	.modal-overlay {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(15, 39, 66, 0.5);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		cursor: pointer;
		z-index: 100000;
		pointer-events: auto;
		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.modal-dialog {
		position: relative;
		pointer-events: auto;
		width: 100%;
		max-width: 500px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		border-radius: 20px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 40px 90px -30px rgba(15, 39, 66, 0.55);
		cursor: default;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.75rem) clamp(1.75rem, 3vw, 2.25rem);
	}

	.modal-close {
		position: absolute;
		top: 1rem;
		right: 1.1rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		line-height: 1;
		border-radius: 10px;
		border: 1px solid var(--border);
		background: var(--surface-muted);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
		z-index: 1;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-close svg {
		width: 18px;
		height: 18px;
	}

	.modal-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.04em;
		margin-bottom: 0.9rem;
	}

	.modal-title {
		font-size: 1.7rem;
		font-weight: 800;
		margin: 0 0 0.5rem;
		color: var(--navy);
	}

	.modal-description {
		font-size: 1rem;
		line-height: 1.6;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.modal-field {
		margin-bottom: 1.1rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.45rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.8rem 0.95rem;
		border-radius: 10px;
		border: 1px solid var(--border-strong);
		background: var(--bg);
		font-family: inherit;
		font-size: 1rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea { resize: vertical; }

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: var(--surface);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.modal-field input:disabled,
	.modal-field textarea:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.btn-block {
		width: 100%;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		-webkit-animation: btn-spin 0.7s linear infinite;
		animation: btn-spin 0.7s linear infinite;
	}

	@-webkit-keyframes btn-spin {
		to { -webkit-transform: rotate(360deg); }
	}

	@keyframes btn-spin {
		to { transform: rotate(360deg); }
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active { transition: opacity 0.25s ease; }

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog { transition: transform 0.25s ease, opacity 0.25s ease; }

	.modal-fade-enter-from,
	.modal-fade-leave-to { opacity: 0; }

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog { transform: translateY(14px) scale(0.98); opacity: 0; }

	@media (max-width: 640px) {
		.modal-dialog { max-width: none; }
	}
</style>